import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { FormattedMessage } from '@flipster/i18n';
import { Badge } from '@flipster/react/design-system/badge';
import Formatted from '@flipster/react/design-utils/formatted';
import CaretDownIcon from '@flipster/react/icon/generated/CaretDownIcon';
import ChevronLeftIcon from '@flipster/react/icon/generated/ChevronLeftIcon';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import ClockIcon from '@flipster/react/icon/generated/ClockIcon';
import { useBreakpoint } from '@flipster/react/misc/responsive';
import { css, cx } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';

import { useAuth } from '../../../../../shared/auth/state';
import { rewardHubV2Bunja } from '../../../state';
import { RegularMission } from '../../../types';
import { scrollArrowButton, selectedItemHover, selectedItemPadding, timelineHorizontalContainer } from '../../styles';
import { getDaysUntilExpiration, getMilestoneTiers, getRewardAmount, getRewardCurrency } from '../../utils';
import { getCoinIconSrc, getMissionCoinIconConfig } from './coin-icon-config';
import SelectedMissionPanel from './selected-mission-panel';
import { useVerticalScroll } from './use-vertical-scroll';

const desktopScrollThreshold = 7;
const tabletScrollThreshold = 5;
const fixedWidthMissionCount = 2;

interface props {
  missions: RegularMission[];
  missionTooltipText?: string;
}

const DesktopMilestoneItem = ({ missions, missionTooltipText }: props) => {
  const { user } = useAuth();
  const { isDesktop, isTablet } = useBreakpoint();

  const { feeVoucherRewardAtom } = useBunja(rewardHubV2Bunja);
  const feeVoucherRewardRecord = useAtomValue(feeVoucherRewardAtom);

  const [selectedMission, setSelectedMission] = useState(0);

  const showScrollArrows =
    (isDesktop && missions.length >= desktopScrollThreshold) || (isTablet && missions.length >= tabletScrollThreshold);
  const isCenterJustify = !showScrollArrows && missions.length <= fixedWidthMissionCount;

  const { listRef, showLeftArrow, showRightArrow, handleScrollLeft, handleScrollRight, updateArrowVisibility } =
    useVerticalScroll(showScrollArrows, missions.length);

  const handleSelectMission = (index: number) => {
    setSelectedMission(index);
  };

  const selectedMissionSpec = missions[selectedMission]?.spec;
  const daysUntilExpiration = selectedMissionSpec
    ? getDaysUntilExpiration(selectedMissionSpec.validityConditions, user?.createdAt)
    : undefined;

  return (
    <div>
      <div className={css({ position: 'relative' })}>
        <div
          ref={listRef}
          onScroll={showScrollArrows ? updateArrowVisibility : undefined}
          className={css(
            isCenterJustify && { display: 'flex', justifyContent: 'center' },
            showScrollArrows && {
              position: 'relative',
              zIndex: 0,
              overflowX: 'auto',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          )}
        >
          <ul
            className={cx(
              hstack({
                width: showScrollArrows || isCenterJustify ? 'max-content' : undefined,
                gap: isCenterJustify ? 136 : 70,
                justify: showScrollArrows ? 'flex-start' : 'space-between',
                px: showScrollArrows ? 48 : 32,
              }),
              timelineHorizontalContainer({ lineInset: showScrollArrows ? 48 : 32 }),
            )}
          >
            {(() => {
              const tiers = getMilestoneTiers(missions.length);
              return missions.map((mission, index) => {
                const spec = mission?.spec;
                if (!spec) return null;
                const completionRewards = spec.completionRewards;
                const completionReward = completionRewards[0];
                const isSelected = selectedMission === index;
                const tier = tiers[index] ?? 1;
                const iconConfig = getMissionCoinIconConfig(mission?.status);
                const iconType = isSelected ? iconConfig.selectedType : iconConfig.notSelectedType;
                const iconSrc = getCoinIconSrc(iconType, tier);
                const textColor = isSelected ? iconConfig.selectedTextColor : iconConfig.notSelectedTextColor;
                return (
                  <li
                    key={`timeline-column-${index}`}
                    onClick={() => handleSelectMission(index)}
                    className={cx(
                      selectedItemPadding,

                      css({
                        backgroundColor: 'background.default.level-1',
                        pb: 20,
                      }),
                    )}
                  >
                    {daysUntilExpiration && (
                      <div className={css({ mb: 6 })}>
                        <Badge type='tint' color='promotion' leadingIcon={<ClockIcon size={16} />}>
                          <FormattedMessage id='Reward_hub.r260219.Limited' />
                        </Badge>
                      </div>
                    )}
                    <button
                      type='button'
                      onClick={() => handleSelectMission(index)}
                      className={cx(
                        stack({
                          gap: 4,
                          align: 'center',
                          position: 'relative',
                          zIndex: 1,
                          flexShrink: 0,
                          py: 12,
                          px: 20,
                          rounded: 8,
                        }),
                        selectedItemHover({ isSelected }),
                      )}
                    >
                      {isSelected && (
                        <CaretDownIcon
                          size={30}
                          color={token('colors.background.default.level-0')}
                          className={css({ pos: 'absolute', bottom: -18 })}
                        />
                      )}
                      <img src={iconSrc} alt='mission icon' width={32} height={32} />
                      <Formatted.Number
                        value={getRewardAmount(completionReward, feeVoucherRewardRecord)}
                        precision={0}
                        toFixed
                        css={{
                          textStyle: 'Title/L/Monospace',
                          color: textColor,
                        }}
                      />
                      <p className={css({ textStyle: 'Text/XS/Regular', color: 'content.default.level-2' })}>
                        {getRewardCurrency(completionReward)}
                      </p>
                    </button>
                  </li>
                );
              });
            })()}
          </ul>
        </div>
        {showScrollArrows && (
          <>
            <button
              type='button'
              onClick={handleScrollLeft}
              disabled={!showLeftArrow}
              className={scrollArrowButton({ side: 'left' })}
              aria-label='Scroll left'
            >
              <ChevronLeftIcon size={20} />
            </button>
            <button
              type='button'
              onClick={handleScrollRight}
              disabled={!showRightArrow}
              className={scrollArrowButton({ side: 'right' })}
              aria-label='Scroll right'
            >
              <ChevronRightIcon size={20} />
            </button>
          </>
        )}
      </div>
      {selectedMissionSpec && (
        <SelectedMissionPanel
          missions={missions}
          selectedIndex={selectedMission}
          missionTooltipText={missionTooltipText}
        />
      )}
    </div>
  );
};

export default DesktopMilestoneItem;
